import { Injectable, signal, computed, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { ApiService } from '../../abstract/api.service';
import { LogbookStats } from '../response-models/logbook-stats.model';
import { LogbookReportDates } from '../response-models/logbook-report-dates.model';
import { isRecord } from '../utils/is-record';
import { countOrZero, nullableString } from '../utils/wire-value-utils';
import { OrganizationContextService } from '../services/organization-context.service';

/**
 * Logbook overview stats: `GET /organizations/:orgId/logbook/stats` (total acres sprayed) and
 * `GET /organizations/:orgId/logbook/report-dates` (when the FAA / state reports were last generated),
 * hub `user-logbook-stats-controller.ts`.
 *
 * Holds the current organization's values as signal state. Failures set `error$` and resolve `null`.
 *
 * Reference: /agent-os/specs/2026-01-27-logbook-overview-page/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class LogbookOverviewService extends ApiService {
  private organizationContext = inject(OrganizationContextService);

  // Signal-based state management
  private statsSignal = signal<LogbookStats | null>(null);
  private reportDatesSignal = signal<LogbookReportDates | null>(null);
  private loadingStatsSignal = signal<boolean>(false);
  private loadingReportDatesSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals for components
  readonly stats$ = computed(() => this.statsSignal());
  readonly reportDates$ = computed(() => this.reportDatesSignal());
  readonly isLoadingStats$ = computed(() => this.loadingStatsSignal());
  readonly isLoadingReportDates$ = computed(() => this.loadingReportDatesSignal());
  readonly isLoading$ = computed(() => this.loadingStatsSignal() || this.loadingReportDatesSignal());
  readonly error$ = computed(() => this.errorSignal());

  constructor() {
    super('logbook');
  }

  /** Load logbook stats (total acres sprayed). */
  async loadStats(): Promise<LogbookStats | null> {
    const stats = await this.load('stats', toLogbookStats, this.loadingStatsSignal, 'Failed to load logbook stats');
    if (stats) this.statsSignal.set(stats);
    return stats;
  }

  /** Load report last-generated dates. */
  async loadReportDates(): Promise<LogbookReportDates | null> {
    const dates = await this.load('report-dates', toReportDates, this.loadingReportDatesSignal, 'Failed to load report dates');
    if (dates) this.reportDatesSignal.set(dates);
    return dates;
  }

  /** Clear error state */
  clearError(): void {
    this.errorSignal.set(null);
  }

  /** GET `organizations/<current org>/logbook/<path>` with the loading flag set; `null` on failure. */
  private async load<R>(
    path: string,
    read: (data: unknown) => R,
    loading: { set(value: boolean): void },
    fallback: string
  ): Promise<R | null> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      console.warn('[LogbookOverviewService] No organization selected');
      return null;
    }
    loading.set(true);
    this.errorSignal.set(null);
    try {
      const request: Observable<R> = this.request('GET', { prefix: ['organizations', orgId], path }, read);
      return await firstValueFrom(request);
    } catch (error: unknown) {
      const apiError = ApiError.fromHttpError(error);
      console.error(`[LogbookOverviewService] ${fallback}:`, apiError.message);
      this.errorSignal.set(apiError.errors[0] ?? fallback);
      return null;
    } finally {
      loading.set(false);
    }
  }
}

function toLogbookStats(data: unknown): LogbookStats {
  const source = isRecord(data) ? data : {};
  return { totalAcresSprayed: countOrZero(source['totalAcresSprayed']) };
}

function toReportDates(data: unknown): LogbookReportDates {
  const source = isRecord(data) ? data : {};
  return {
    faaLastGenerated: nullableString(source['faaLastGenerated']) ?? null,
    stateLastGenerated: nullableString(source['stateLastGenerated']) ?? null,
  };
}
